// Decision Console read model (DR-5).
//
// Strategy-scoped governed read model for the founder's decision console. Every number is a
// governed binding derived from RealizationCommitments (DR-1), DR-2 realization drift, and the
// governed cost basis on the approved decision artifact — or an honest, labeled gap. No mock
// data, no LLM computation. Modeled seed rows carry RENDER-OPEN posture labels.
import type { PublishedRealizationPaceReceipt } from './approved-formulas';
import type { SourceClassLabel } from './source-class-label';
import type { WatchEvent } from './leading-indicator-watch';

export const DECISION_CONSOLE_READ_MODEL_SCHEMA_VERSION = 'decision_console_read_model_v1';

/**
 * RENDER-OPEN posture for a row: data is always shown, labeled by how governed it is
 * (founder law 2026-07-06). `gap` is reserved for genuinely-absent data / failed truth checks.
 */
export type DecisionConsoleRenderPosture =
  | 'certified'
  | 'approved_source'
  | 'modeled'
  | 'pending_owner_validation'
  | 'planning_view'
  | 'pre_receipt'
  | 'gap';

/** Governed DR-2 drift classification (kernel verdict), plus `gap` when it cannot be computed. */
export type DecisionConsoleDriftStatus =
  | 'on_track'
  | 'watch'
  | 'drifting'
  | 'amendment_required'
  | 'gap';

export type DecisionConsoleValueBasis =
  | 'gross_fully_loaded'
  | 'net_ebitda'
  | 'approved_value_binding';

/**
 * Closed recommendation verb set (founder law). The model may propose wording/ranking; code
 * computes every dollar/week consequence from governed commitment + decision cost-basis data.
 */
export const DECISION_CONSOLE_OPTION_VERBS = [
  'reduce_intake',
  'defer',
  'reallocate',
  'add_capacity',
  'accept_risk',
] as const;
export type DecisionConsoleOptionVerb = (typeof DECISION_CONSOLE_OPTION_VERBS)[number];

/**
 * THE canonical Decision Console bet/docket identity key (#4631 Codex
 * 5317706334): one bet = one commitment metric, keyed by the Decision Ledger
 * decisionId plus the governed metricKey. `DecisionConsoleBetV1.key` and
 * `DecisionConsoleDocketItemV1.linkedBetKey` are BOTH constructed here — any
 * consumer joining a docket action to a commitment (e.g. the Drift
 * enumeration recommended move) MUST use this constructor, never a re-typed
 * template or a title/prose match.
 */
export function decisionConsoleBetKey(
  decisionId: string,
  metricKey: string,
): string {
  return `bet-${decisionId}-${metricKey}`;
}

/** How the executive verdict / advisory sentence was produced. */
export type DecisionConsoleNarrationMode = 'deterministic_template' | 'llm';

/** One point on the committed curve, with the governed actual at the same as-of when present. */
export type DecisionConsoleCurvePointV1 = {
  asOf: string;
  plannedValue: number;
  actualValue: number | null;
  isLatestActual: boolean;
};

/** The committed-curve burn-down: planned curve vs governed actuals for one metric. */
export type DecisionConsoleBurnDownV1 = {
  metricKey: string;
  unit: string;
  direction: 'increase' | 'decrease' | 'hold';
  baselineValue: number;
  targetValue: number;
  targetDate: string;
  points: DecisionConsoleCurvePointV1[];
  latestActualAsOf: string | null;
  latestActualValue: number | null;
  latestExpectedValue: number | null;
};

/** DR-2 realization drift for one bet. Numbers are governed bindings or labeled gaps. */
export type DecisionConsoleDriftV1 = {
  status: DecisionConsoleDriftStatus;
  /** Governed units behind the committed curve at the latest actual (>= 0). */
  behindUnits: number | null;
  /** Kernel behind-plan ratio (normalized). */
  behindRatio: number | null;
  /** Governed value at risk vs the curve, via realization_projected_miss_v1@1.0.0. */
  projectedMissUsd: number | null;
  /** Basis label for the governed value rendered beside `projectedMissUsd`. */
  valueBasis: DecisionConsoleValueBasis | null;
  valueLabel: string | null;
  shadowMode: boolean;
  thresholdSource: string | null;
  formulaId: string | null;
  formulaVersion: string | null;
  /** Governed $/unit used to price the miss, plus the cited derivation basis. */
  valuePerUnit: number | null;
  valuePerUnitBasis: string | null;
  /** Governed share of the committed reduction actually achieved by the latest as-of (0..1). */
  achievedPaceRatio: number | null;
  /** Source-owned incremental weekly movement required to erase today's curve deficit. */
  requiredCatchUpIncrementPerWeek?: number | null;
  /** Identity of the source-owned pace receipt behind the recovery-option copy. */
  paceFormulaId?: string | null;
  paceFormulaVersion?: string | null;
  /**
   * The FULL console-validated published pace receipt (identity + digests), when one resolved
   * at this reporting cut. Downstream governed surfaces (the Ask Helix realization answer via
   * the console-bet adapter) re-validate and cite it instead of re-deriving pace, so the answer
   * binds the console's now-clamped cut. Null when `gapReasonCode === 'pace_receipt_missing'`.
   */
  paceReceipt?: PublishedRealizationPaceReceipt | null;
  /**
   * SECONDARY exposure line: behind-curve units priced at the gross fully-loaded cost per unit
   * from the approved decision cost basis. NEVER the headline — `projectedMissUsd` (the basis
   * the commitment was underwritten on) stays the headline; this is the labeled companion.
   */
  grossFullyLoadedExposureUsd?: number | null;
  /** Cited derivation basis for `grossFullyLoadedExposureUsd`. */
  grossExposureBasis?: string | null;
  asOf: string | null;
  /** Reason the drift $ (or verdict) could not be computed, when applicable. */
  gapReasonCode: string | null;
  gapMessage: string | null;
  /** Deterministic advisory sentence assembled from the structured drift facts. */
  verdictSentence: string;
  narrationMode: DecisionConsoleNarrationMode;
};

/** One governed consequence of choosing an option. Qualitative-only when no $ is computable. */
export type DecisionConsoleOptionConsequenceV1 = {
  label: string;
  valueUsd: number | null;
  qualitative: boolean;
  basis: string;
};

/** A priced decision option (SCENARIO-OPEN: computed freely, committed only on approval). */
export type DecisionConsoleOptionV1 = {
  verb: DecisionConsoleOptionVerb;
  headline: string;
  detail: string;
  recommended?: boolean;
  economicsLine?: string;
  ctaLabel?: string;
  consequences: DecisionConsoleOptionConsequenceV1[];
  commitGated: true;
  posture: DecisionConsoleRenderPosture;
};

/** A strategic bet = one RealizationCommitment, its live drift, and its priced options. */
export type DecisionConsoleBetV1 = {
  key: string;
  commitmentId: string;
  decisionId: string;
  leverId: string;
  name: string;
  /** Explicit governed binding to the active package decision keyspace. */
  packageInitiativeKey?: string | null;
  ownerUserId?: string | null;
  ownerLabel: string;
  /** Deterministic drill affordance for the commitment's governed pace. */
  drillQuestion?: string | null;
  sourceClass: string;
  sourceClassLabel: SourceClassLabel;
  posture: DecisionConsoleRenderPosture;
  burnDown: DecisionConsoleBurnDownV1;
  drift: DecisionConsoleDriftV1;
  options: DecisionConsoleOptionV1[];
  evidenceRefs: string[];
};

/** Risk-radar rollup computed from the real drift/gate states across all bets. */
export type DecisionConsoleRiskRadarV1 = {
  tracked: number;
  onTrack: number;
  watch: number;
  drifting: number;
  amendmentRequired: number;
  gap: number;
  valueAtRiskUsd: number | null;
  valueAtRiskBasis: DecisionConsoleValueBasis | null;
  valueAtRiskLabel: string | null;
};

export type DecisionConsoleExecutiveVerdictV1 = {
  headline: string;
  bindingConstraint: string;
  decisionThatChangesOutcome: string;
  narrationMode: DecisionConsoleNarrationMode;
};

/** Computed scenario awaiting commitment (SCENARIO-OPEN world). */
export type DecisionConsoleDocketItemV1 = {
  id: string;
  question: string;
  linkedBetKey: string;
  linkedBetName: string;
  verbOptions: DecisionConsoleOptionVerb[];
  ownerLabel?: string;
  dollarsAtStakeUsd?: number | null;
  dollarBasisLabel?: string | null;
  recommendedVerb?: DecisionConsoleOptionVerb | null;
  ctaLabel?: string;
  dueLabel: string;
};

export type DecisionConsoleReadModelStatus = 'ok' | 'empty';

export type DecisionConsoleReadModelV1 = {
  schemaVersion: typeof DECISION_CONSOLE_READ_MODEL_SCHEMA_VERSION;
  tenantId: string;
  strategyId: string;
  generatedAt: string;
  status: DecisionConsoleReadModelStatus;
  /** Set when there are no commitments; the console renders this, never mock content. */
  emptyStateMessage: string | null;
  bets: DecisionConsoleBetV1[];
  riskRadar: DecisionConsoleRiskRadarV1;
  executiveVerdict: DecisionConsoleExecutiveVerdictV1 | null;
  decisionDocket: DecisionConsoleDocketItemV1[];
  /** Fired leading-indicator watches still relevant to this console cycle. */
  watchEvents?: readonly WatchEvent[];
  /** True only when HELIX_CONTINUOUS_MONITORING_ENABLED is set in this process (narration seam). */
  monitoringNarrationEnabled: boolean;
};

export const DECISION_CONSOLE_EMPTY_STATE_MESSAGE =
  'No commitments yet — commission from Ask Helix';

export const DECISION_CONSOLE_OWNER_EMPTY_STATE_MESSAGE = 'No commitments assigned';
